import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Row from './components/Row';
import DetailModalContent from './components/DetailModalContent';
import DetailModalTitle from './components/DetailModalTitle';
import TableBox from '../../../components/TableBox';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import {
  AuditLogFilterOptions, AuditLogItem, AuditLogListFilters, SavedAuditLogFilters,
} from '../../../interfaces/auditLog.interface';
import { buildHeadCells, getStartEndOfMonth } from '../../../utils/methods';
import { VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING } from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import useUrlParams from '../../../hooks/useUrlParams';
import useFetch from '../../../hooks/useFetch';
import { usePersistentFilters } from '../../../hooks/usePersistentFilters';
import AuditLogService from '../../../services/auditLog.service';
import { SortOrder, Modules } from '../../../enums';

// Filters saved before "Module" was split into Entity and Module have no entity: what they called the module
// was the kind of record, so it becomes the entity and no functionality is chosen.
const withEntity = (saved: SavedAuditLogFilters): AuditLogListFilters => (
  saved.entity !== undefined
    ? { ...saved, entity: saved.entity }
    : { ...saved, entity: saved.module ?? '', module: '' }
);

const List: React.FC = () => {
  const { t } = useTranslation();

  const defaultFilters: AuditLogListFilters = useMemo(() => ({
    searchText: '',
    entity: '',
    module: '',
    event: '',
    user: '',
    systemOnly: false,
    ...getStartEndOfMonth(),
  }), []);

  const { initialFilters: savedFilters, saveFilters, resetFilters } = usePersistentFilters<AuditLogListFilters>('filters_audit_logs', defaultFilters);
  const initialFilters = useMemo(() => withEntity(savedFilters), [savedFilters]);
  const [filters, setFilters] = useState<AuditLogListFilters>(initialFilters);
  const [counter, setCounter] = useState<number>(0);
  const [detail, setDetail] = useState<{ open: boolean; item: AuditLogItem | null }>({ open: false, item: null });
  const [filterOptions, setFilterOptions] = useState<AuditLogFilterOptions>({ entities: [], modules: [], events: [], users: [] });

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.occurredAt,
    VALIDATION_FIELD_MAPPING.actor,
    VALIDATION_FIELD_MAPPING.auditEvent,
    VALIDATION_FIELD_MAPPING.auditEntity,
    VALIDATION_FIELD_MAPPING.auditModule,
    VALIDATION_FIELD_MAPPING.auditSubject,
    VALIDATION_FIELD_MAPPING.auditDescription,
    VALIDATION_FIELD_MAPPING.auditDetails,
  ], 'auditLogList');

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.desc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.occurredAt,
  });

  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({ type: Modules.auditLogs, uri: Modules.auditLogs, perPage, page, order, orderBy });

  const { total, data: logs, loading } = useFetch({
    service: AuditLogService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.auditLogList as any)[orderBy] || orderBy,
      order,
      ...filters,
    },
    dependency: [
      order, orderBy, page, perPage,
      filters.searchText, filters.entity, filters.module, filters.event, filters.user, filters.systemOnly, filters.startDate, filters.endDate,
      counter,
    ],
  });

  // The choices for the entity, module, event and user filters come from what exists in the audit log
  useEffect(() => {
    AuditLogService.filters()
      .then((options: AuditLogFilterOptions) => setFilterOptions(options))
      .catch(() => setFilterOptions({ entities: [], modules: [], events: [], users: [] }));
  }, [counter]);

  const handleApplyFilters = useCallback((newFilters: AuditLogListFilters) => {
    setPage(1);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters(defaultFilters);
    resetFilters();
  }, [defaultFilters, resetFilters, setPage]);

  const handleViewClick = useCallback((item: AuditLogItem) => setDetail({ open: true, item }), []);
  const handleViewClose = useCallback(() => setDetail({ open: false, item: null }), []);

  const headerSection = useMemo(() => (
    <div className="col-md-12 header-section-div">
      <div className="row">
        <div className="col-sm-12 px-3 py-4 d-flex align-items-center">
          <i className="ri-history-line ri-22px" aria-hidden="true"></i>
          <h5 className="mx-3 my-0">{t('auditLogs.list.pageTexts.table.title')} ({total})</h5>
        </div>
      </div>
    </div>
  ), [t, total]);

  const values: TableContextProvider = useMemo(() => ({
    rowProps: { onViewClick: handleViewClick },
    headerSection,
    row: Row,
    data: logs || [],
    pagination: true,
    headCells,
    order,
    orderBy,
    page,
    perPage,
    total,
    loading,
    noDataFoundMessage: t('auditLogs.list.pageTexts.table.noDataFoundMessage'),
    setPage,
    setPerPage,
    setOrder,
    setOrderBy,
  }), [t, handleViewClick, headerSection, logs, headCells, order, orderBy, page, perPage, total, loading, setPage, setPerPage, setOrder, setOrderBy]);

  return (
    <>
      <Header onRefresh={() => setCounter((prev) => prev + 1)} />

      <Filters
        entities={filterOptions.entities}
        modules={filterOptions.modules}
        events={filterOptions.events}
        users={filterOptions.users}
        initialFilters={initialFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <TableBox tableValues={values} />

      <Modal
        open={detail.open}
        onClose={handleViewClose}
        maxWidth="sm"
        titleClassName="text-start"
        actionClassName="justify-content-end"
        title={<DetailModalTitle item={detail.item} onClose={handleViewClose} />}
        content={<DetailModalContent item={detail.item} />}
        action={(
          <Button
            className="btn btn-outline-secondary"
            text={t('auditLogs.list.pageTexts.detail.close')}
            onClick={handleViewClose}
          />
        )}
      />
    </>
  );
};

export default List;
